"use client";

import { Play } from "@phosphor-icons/react";
import { motion } from "framer-motion";

import { cn } from "@/lib/utils";

import type { WordOfWeekTranslations, WordOfWeekVideo } from "./word-of-the-week-types";

type WordOfWeekArchiveProps = {
  selectedId: string;
  translations: WordOfWeekTranslations;
  videos: WordOfWeekVideo[];
  onSelect: (video: WordOfWeekVideo) => void;
};

const formatRowDate = (timestamp: number) => {
  const d = new Date(timestamp);
  return {
    day: new Intl.DateTimeFormat("en-CA", { day: "2-digit" }).format(d),
    month: new Intl.DateTimeFormat("en-CA", { month: "short" }).format(d),
    year: new Intl.DateTimeFormat("en-CA", { year: "numeric" }).format(d),
  };
};

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.045, delayChildren: 0.1 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring" as const, stiffness: 120, damping: 20 },
  },
};

export function WordOfWeekArchive({
  selectedId,
  translations,
  videos,
  onSelect,
}: WordOfWeekArchiveProps) {
  return (
    <section>
      {/* Header — asymmetric, left-heavy */}
      <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <span className="text-[10px] font-black uppercase tracking-[0.35em] text-brand-red dark:text-brand-gold">
            {translations.archiveLabel}
          </span>
          <h2 className="mt-3 font-display text-4xl font-black tracking-tighter text-zinc-950 dark:text-white md:text-5xl">
            {translations.archiveTitle}
          </h2>
        </div>
        <p className="max-w-md self-end text-sm leading-relaxed text-zinc-600 dark:text-zinc-400 lg:text-right">
          {translations.archiveBody}
        </p>
      </div>

      {/* Divider */}
      <div className="mt-8 h-px bg-zinc-200 dark:bg-white/10" />

      {/* Episode list — clean rows, no cards */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="divide-y divide-zinc-150 dark:divide-white/[0.06]"
      >
        {videos.map((video, index) => {
          const isSelected = video._id === selectedId;
          const { day, month, year } = formatRowDate(video.uploadedAt);
          const episodeNum = String(videos.length - index).padStart(2, "0");

          return (
            <motion.button
              key={video._id}
              type="button"
              variants={rowVariants}
              onClick={() => onSelect(video)}
              className={cn(
                "group flex w-full items-center gap-4 py-5 text-left transition-colors duration-200 md:gap-8 md:py-6",
                isSelected
                  ? "text-brand-red dark:text-brand-gold"
                  : "text-zinc-600 hover:text-zinc-950 dark:text-zinc-600 dark:hover:text-white"
              )}
            >
              {/* Episode number */}
              <span className="w-8 flex-none font-display text-xl font-black tabular-nums tracking-tight md:w-10 md:text-2xl">
                {episodeNum}
              </span>

              {/* Date block — stacked day/month for visual punch */}
              <div className="flex w-24 flex-none items-baseline gap-2 md:w-32">
                <span
                  className={cn(
                    "font-display text-3xl font-black tabular-nums leading-none tracking-tight transition-colors md:text-4xl",
                    isSelected
                      ? "text-zinc-950 dark:text-white"
                      : "text-zinc-500 group-hover:text-zinc-950 dark:text-zinc-700 dark:group-hover:text-white"
                  )}
                >
                  {day}
                </span>
                <div className="flex flex-col text-[10px] font-bold uppercase tracking-[0.2em]">
                  <span>{month}</span>
                  <span className="text-zinc-600 dark:text-zinc-700">{year}</span>
                </div>
              </div>

              {/* Spacer line */}
              <div className="hidden h-px flex-1 bg-zinc-100 dark:bg-white/[0.04] sm:block" />

              {/* Status label */}
              <span className="hidden text-[10px] font-black uppercase tracking-[0.25em] sm:inline-block">
                {isSelected ? translations.selectedLabel : translations.watchButton}
              </span>

              {/* Play indicator */}
              <span
                className={cn(
                  "flex h-9 w-9 flex-none items-center justify-center rounded-full transition-all duration-200 md:h-10 md:w-10",
                  isSelected
                    ? "bg-brand-red text-white dark:bg-brand-gold dark:text-zinc-950"
                    : "bg-zinc-100 text-zinc-400 group-hover:bg-zinc-950 group-hover:text-white dark:bg-white/[0.04] dark:text-zinc-600 dark:group-hover:bg-white dark:group-hover:text-zinc-950"
                )}
              >
                <Play size={14} weight="fill" />
              </span>
            </motion.button>
          );
        })}
      </motion.div>
    </section>
  );
}
